import { useEffect } from 'react';
import { create } from 'zustand';
import { clientAuthorizedGet, clientAuthorizedPatch } from 'utils/fetchUtils';
import { API_ROUTES } from 'utils/constants';
import { useUserProfileStore } from './userProfileStore';

type UserPreferencesResponse = {
  betaEnabled: boolean;
  dashboardQuickLinksDisabled?: boolean;
};

interface UserPreferencesStore {
  userId: string | null;
  betaEnabled: boolean;
  isLoading: boolean;
  setBetaEnabled: (enabled: boolean) => Promise<void>;
  initForUser: (userId: string | null) => Promise<void>;
}

export const useUserPreferencesStore = create<UserPreferencesStore>((set) => ({
  userId: null,
  betaEnabled: false,
  isLoading: false,
  setBetaEnabled: async (enabled) => {
    const previousValue = useUserPreferencesStore.getState().betaEnabled;
    set({ betaEnabled: enabled });
    try {
      const response = await clientAuthorizedPatch<UserPreferencesResponse>(
        API_ROUTES.USER.PREFERENCES,
        { betaEnabled: enabled },
      );
      set({ betaEnabled: Boolean(response.betaEnabled) });
    } catch {
      // Restore previous UI state if persistence fails.
      set({ betaEnabled: previousValue });
      throw new Error('Failed to save app settings');
    }
  },
  initForUser: async (userId) => {
    if (!userId) {
      set({ userId: null, betaEnabled: false, isLoading: false });
      return;
    }
    set({ userId, isLoading: true });
    try {
      const response = await clientAuthorizedGet<UserPreferencesResponse>(
        API_ROUTES.USER.PREFERENCES,
      );
      set({ betaEnabled: Boolean(response.betaEnabled), isLoading: false });
    } catch {
      set({ betaEnabled: false, isLoading: false });
    }
  },
}));

export function useUserPreferencesInit(): void {
  const profile = useUserProfileStore((s) => s.profile);
  const initForUser = useUserPreferencesStore((s) => s.initForUser);

  useEffect(() => {
    void initForUser(profile?.id ?? null);
  }, [profile?.id, initForUser]);
}
